import { useEffect, useMemo } from 'react';

import { useMyStatusQuery } from '@/entities/me';
import { ownsDemoCopies, useDemoRosterQuery } from '@/entities/sandbox-demo';

import type { PatientNavigatorItemData } from './types';
import type { ScopedPatientTab, ScopedPatientTabOption } from './scoped-tabs';
import {
  buildScopedPatientTabs,
  filterPatientsByScopedTab,
  resolveScopedPatientTab,
} from './scoped-tabs';

interface UseScopedPatientTabsParams {
  patients: PatientNavigatorItemData[];
  activeTab: ScopedPatientTab;
  isPatientsLoading: boolean;
  onTabChange: (tab: ScopedPatientTab) => void;
}

interface UseScopedPatientTabsResult {
  availableTabs: ScopedPatientTabOption[];
  effectiveTab: ScopedPatientTab | null;
  filtered: PatientNavigatorItemData[];
}

/**
 * Shared hook that encapsulates scoped patient tab resolution.
 *
 * Used by both PatientNavigator (workspace sidebar) and PatientQueue
 * (dashboard) so the auto-resolution + parent-propagation logic lives
 * in a single place.
 */
export function useScopedPatientTabs({
  patients,
  activeTab,
  isPatientsLoading,
  onTabChange,
}: UseScopedPatientTabsParams): UseScopedPatientTabsResult {
  const { data: myStatus, isPending: isStatusPending } = useMyStatusQuery();
  const isEvaluationAdmin = myStatus?.subsystemAccess?.['evaluation_admin'] === true;

  // Demo copies never carry an open request, so Urgent and Routine are empty
  // for every clinician on sandbox. A non-admin would be left with no tab at
  // all and no way to reach their own roster; owning demo copies is therefore
  // grounds for the All tab on its own. `isLoading` rather than `isPending`:
  // the roster query is disabled off sandbox, and a disabled query is pending
  // forever.
  const { data: roster, isLoading: isRosterLoading } = useDemoRosterQuery();
  const includeAllTab = isEvaluationAdmin || ownsDemoCopies(roster);

  const availableTabs = useMemo(
    () => buildScopedPatientTabs(patients, includeAllTab),
    [patients, includeAllTab]
  );

  const effectiveTab = useMemo(
    () => resolveScopedPatientTab(activeTab, availableTabs),
    [activeTab, availableTabs]
  );

  useEffect(() => {
    if (isPatientsLoading || isStatusPending || isRosterLoading) {
      return;
    }
    if (effectiveTab === null || effectiveTab === activeTab) {
      return;
    }
    onTabChange(effectiveTab);
  }, [activeTab, effectiveTab, isPatientsLoading, isRosterLoading, isStatusPending, onTabChange]);

  const filtered = useMemo(
    () => (effectiveTab === null ? [] : filterPatientsByScopedTab(patients, effectiveTab)),
    [effectiveTab, patients]
  );

  return { availableTabs, effectiveTab, filtered };
}
